/**
 * RibbonPool — continuous tapered trails, sampled from a thing's own path.
 *
 * Pass 1 drew a bullet's wake as one sprite every 30 ms, which at 46 u/s is a
 * puff every 1.4 units: a dotted line. A trail is a STRIP: the last few
 * positions of the projectile joined into one tapered ribbon, a white core down
 * the middle, the identity colour toward the edges, ink at the very edge, and
 * a ragged eroded tail. Every ribbon in the game is one region of one geometry
 * — one draw call however many rounds are in the air.
 *
 * The strip lies flat (its width is perpendicular to the path in the ground
 * plane): from the 55° camera that foreshortens by 0.82 and never turns
 * edge-on for a round flying across the deck, and it needs no camera.
 *
 * A ribbon is `acquire`d by the thing that flies, `push`ed every frame, and
 * `release`d when the thing dies; it then drains — the head stops, the tail
 * erodes through it over ~0.14 s — and the slot returns to the pool.
 */

import * as THREE from 'three/webgpu';
import { uv, float, smoothstep, step, attribute, vertexColor, mix } from 'three/tsl';
import { cloud, erode, palette } from './CelLook.js';

/** Points remembered per ribbon: the strip has POINTS - 1 segments. */
const POINTS = 12;
/** A push closer than this to the head is merged, so a slow thing keeps a short trail. */
const MIN_STEP = 0.06;
/** How long a released ribbon takes to drain. */
const DRAIN_S = 0.14;
const HIDDEN_Y = -1000;

function buildMaterial() {
  const mat = new THREE.MeshBasicNodeMaterial();
  const tint = vertexColor();
  // No bloom on a wake: a trail is paint, not light.
  const c = palette(tint, float(1.1));
  // uv.x: 0..1 across the strip; uv.y: 0 at the head, 1 at the tail.
  const across = uv().x.sub(0.5).abs().mul(2);
  const along = uv().y;
  const fade = attribute('aFade', 'float');
  const seed = attribute('aSeed', 'float');
  // A thin white line down the middle that dies out by mid-length, a hot
  // band around it near the head, the body colour, ink at the edges.
  const coreW = float(0.16).mul(float(1).sub(along.mul(2.5)).clamp(0, 1));
  let col = mix(c.body, c.hot, smoothstep(float(0.4), float(0.2), across).mul(float(1).sub(along.mul(3)).clamp(0, 1)));
  col = mix(col, c.core, smoothstep(coreW.add(0.05), coreW, across).mul(step(float(0.01), coreW)));
  col = mix(col, c.ink, smoothstep(float(0.78), float(0.9), across));
  const n = cloud(uv().mul(float(3.0)).add(seed), seed);
  // The tail erodes away: progress rises along the strip and as the ribbon drains.
  const progress = along.mul(along).mul(0.95).add(float(1).sub(fade)).clamp(0, 1);
  const e = erode(n, progress, { edge: 0.06, front: 0.14 });
  mat.colorNode = mix(col, c.core, e.front);
  mat.opacityNode = e.alive.mul(smoothstep(float(1.0), float(0.94), across));
  mat.transparent = true;
  mat.depthWrite = false;
  mat.side = THREE.DoubleSide;
  mat.fog = false;
  return mat;
}

export class RibbonPool {
  constructor(capacity) {
    this.capacity = capacity;
    this.cap = capacity;
    const verts = capacity * POINTS * 2;
    this.pos = new Float32Array(verts * 3);
    this.col = new Float32Array(verts * 3);
    this.uvs = new Float32Array(verts * 2);
    this.fade = new Float32Array(verts);
    this.seed = new Float32Array(verts);
    for (let i = 0; i < verts; i++) this.pos[i * 3 + 1] = HIDDEN_Y;
    const index = new Uint32Array(capacity * (POINTS - 1) * 6);
    let k = 0;
    for (let r = 0; r < capacity; r++) {
      const base = r * POINTS * 2;
      for (let s = 0; s < POINTS - 1; s++) {
        const v0 = base + s * 2;
        index[k++] = v0; index[k++] = v0 + 1; index[k++] = v0 + 2;
        index[k++] = v0 + 1; index[k++] = v0 + 3; index[k++] = v0 + 2;
      }
    }
    const geometry = new THREE.BufferGeometry();
    this.posAttr = new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage);
    this.colAttr = new THREE.BufferAttribute(this.col, 3).setUsage(THREE.DynamicDrawUsage);
    this.uvAttr = new THREE.BufferAttribute(this.uvs, 2).setUsage(THREE.DynamicDrawUsage);
    this.fadeAttr = new THREE.BufferAttribute(this.fade, 1).setUsage(THREE.DynamicDrawUsage);
    this.seedAttr = new THREE.BufferAttribute(this.seed, 1).setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('position', this.posAttr);
    geometry.setAttribute('color', this.colAttr);
    geometry.setAttribute('uv', this.uvAttr);
    geometry.setAttribute('aFade', this.fadeAttr);
    geometry.setAttribute('aSeed', this.seedAttr);
    geometry.setIndex(new THREE.BufferAttribute(index, 1));
    this.mesh = new THREE.Mesh(geometry, buildMaterial());
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
    this.mesh.renderOrder = 7;
    this.mesh.name = 'ribbons';
    this.mesh.matrixAutoUpdate = false;
    this.mesh.userData.noOutline = true;
    /** Per ribbon: state, colour, width, length, its ring of points. */
    this.ribbons = [];
    for (let i = 0; i < capacity; i++) {
      this.ribbons.push({ live: false, draining: 0, r: 1, g: 1, b: 1, width: 0.3, length: 3, seed: 0, n: 0, head: 0, pts: new Float32Array(POINTS * 3) });
    }
    this.live = 0;
    this.stats = { acquired: 0, refused: 0 };
    this._dirty = false;
  }

  attach(scene) {
    scene.add(this.mesh);
    return this;
  }

  setCap(n) {
    this.cap = Math.max(0, Math.min(this.capacity, Math.floor(n)));
    for (let i = this.cap; i < this.capacity; i++) if (this.ribbons[i].live) this._free(i);
  }

  /**
   * A ribbon in `color` (linear r,g,b already normalised), `width` units at the
   * head, tapering to nothing `length` units back along the path. Returns the
   * handle, or -1 when the tier's ribbons are all in use — the thing still
   * flies; it just leaves no wake.
   */
  acquire(r, g, b, width, length) {
    for (let i = 0; i < this.cap; i++) {
      const rb = this.ribbons[i];
      if (rb.live) continue;
      rb.live = true;
      rb.draining = 0;
      rb.r = r; rb.g = g; rb.b = b;
      rb.width = width;
      rb.length = Math.max(0.2, length);
      rb.seed = Math.random() * 10;
      rb.n = 0;
      rb.head = 0;
      this.live++;
      this.stats.acquired++;
      return i;
    }
    this.stats.refused++;
    return -1;
  }

  /** The thing moved here. */
  push(id, x, y, z) {
    if (id < 0) return;
    const rb = this.ribbons[id];
    if (!rb.live || rb.draining > 0) return;
    if (rb.n > 0) {
      const h = rb.head * 3;
      const dx = x - rb.pts[h];
      const dy = y - rb.pts[h + 1];
      const dz = z - rb.pts[h + 2];
      if (dx * dx + dy * dy + dz * dz < MIN_STEP * MIN_STEP) {
        // Too close: move the head instead of adding a point.
        rb.pts[h] = x; rb.pts[h + 1] = y; rb.pts[h + 2] = z;
        this._dirty = true;
        return;
      }
      rb.head = (rb.head + 1) % POINTS;
    }
    const h = rb.head * 3;
    rb.pts[h] = x; rb.pts[h + 1] = y; rb.pts[h + 2] = z;
    if (rb.n < POINTS) rb.n++;
    this._dirty = true;
  }

  /** The thing died: the ribbon drains and frees itself. */
  release(id) {
    if (id < 0) return;
    const rb = this.ribbons[id];
    if (!rb.live || rb.draining > 0) return;
    rb.draining = 1e-6;
  }

  _free(i) {
    const rb = this.ribbons[i];
    rb.live = false;
    rb.draining = 0;
    rb.n = 0;
    this.live--;
    const base = i * POINTS * 2;
    for (let v = 0; v < POINTS * 2; v++) this.pos[(base + v) * 3 + 1] = HIDDEN_Y;
    this._dirty = true;
  }

  update(dt) {
    if (this.live === 0) {
      if (this.mesh.visible) {
        this.mesh.visible = false;
      }
      return;
    }
    let any = false;
    for (let i = 0; i < this.capacity; i++) {
      const rb = this.ribbons[i];
      if (!rb.live) continue;
      if (rb.draining > 0 && dt > 0) {
        rb.draining += dt;
        if (rb.draining >= DRAIN_S) {
          this._free(i);
          continue;
        }
        this._dirty = true;
      }
      any = true;
      this._write(i, rb);
    }
    this.mesh.visible = any;
    if (this._dirty) {
      this.posAttr.needsUpdate = true;
      this.colAttr.needsUpdate = true;
      this.uvAttr.needsUpdate = true;
      this.fadeAttr.needsUpdate = true;
      this.seedAttr.needsUpdate = true;
      this._dirty = false;
    }
  }

  /** Rebuild one ribbon's strip: head first, width and alpha tapering by path distance. */
  _write(i, rb) {
    const base = i * POINTS * 2;
    const fade = rb.draining > 0 ? 1 - rb.draining / DRAIN_S : 1;
    const pts = rb.pts;
    let dist = 0;
    let px = 0; let py = 0; let pz = 0;
    for (let k = 0; k < POINTS; k++) {
      const v = base + k * 2;
      if (k >= rb.n) {
        // Unused points collapse onto the last real one so the strip ends cleanly.
        const last = base + Math.max(0, rb.n - 1) * 2;
        for (let s = 0; s < 2; s++) {
          this.pos[(v + s) * 3] = this.pos[(last + s) * 3];
          this.pos[(v + s) * 3 + 1] = this.pos[(last + s) * 3 + 1];
          this.pos[(v + s) * 3 + 2] = this.pos[(last + s) * 3 + 2];
          this.uvs[(v + s) * 2] = s;
          this.uvs[(v + s) * 2 + 1] = 1;
          this.fade[v + s] = fade;
          this.seed[v + s] = rb.seed;
          this.col[(v + s) * 3] = rb.r; this.col[(v + s) * 3 + 1] = rb.g; this.col[(v + s) * 3 + 2] = rb.b;
        }
        continue;
      }
      // Walk back from the head.
      const idx = ((rb.head - k) % POINTS + POINTS) % POINTS;
      const x = pts[idx * 3];
      const y = pts[idx * 3 + 1];
      const z = pts[idx * 3 + 2];
      // Direction toward the head (for the width's perpendicular).
      let dx; let dz;
      if (k === 0) {
        const nxt = ((rb.head - 1) % POINTS + POINTS) % POINTS;
        dx = rb.n > 1 ? x - pts[nxt * 3] : 1;
        dz = rb.n > 1 ? z - pts[nxt * 3 + 2] : 0;
      } else {
        dx = px - x;
        dz = pz - z;
        // Distance along the trail is 3D: a shell dropping straight down moves
        // only in y, and a ground-plane measure left its trail at distance 0 —
        // full width, never tapering, never eroding: a flat vertical bar.
        dist += Math.hypot(px - x, py - y, pz - z);
      }
      const len = Math.hypot(dx, dz);
      if (len > 1e-5) { dx /= len; dz /= len; } else { dx = 1; dz = 0; }
      const k01 = Math.min(1, dist / rb.length);
      const half = rb.width * 0.5 * Math.pow(1 - k01, 0.5);
      // Perpendicular in the ground plane.
      const ox = -dz * half;
      const oz = dx * half;
      this.pos[v * 3] = x - ox; this.pos[v * 3 + 1] = y; this.pos[v * 3 + 2] = z - oz;
      this.pos[(v + 1) * 3] = x + ox; this.pos[(v + 1) * 3 + 1] = y; this.pos[(v + 1) * 3 + 2] = z + oz;
      this.uvs[v * 2] = 0; this.uvs[v * 2 + 1] = k01;
      this.uvs[(v + 1) * 2] = 1; this.uvs[(v + 1) * 2 + 1] = k01;
      this.fade[v] = fade; this.fade[v + 1] = fade;
      this.seed[v] = rb.seed; this.seed[v + 1] = rb.seed;
      for (let s = 0; s < 2; s++) {
        this.col[(v + s) * 3] = rb.r; this.col[(v + s) * 3 + 1] = rb.g; this.col[(v + s) * 3 + 2] = rb.b;
      }
      px = x; py = y; pz = z;
    }
  }

  clear() {
    for (let i = 0; i < this.capacity; i++) if (this.ribbons[i].live) this._free(i);
    this.live = 0;
    this.mesh.visible = false;
    this.posAttr.needsUpdate = true;
  }

  dispose() {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
  }
}

export { POINTS as RIBBON_POINTS };
